/**
 * 统一的业务领域提供者
 *
 * @description
 * 此组件旨在将所有独立的业务领域提供者（如 Auth, Subscription, Credits 等）
 * 组合到一个统一的位置。
 *
 * @rationale
 * 随着业务复杂度的增加，不同领域之间可能需要协作。例如，订阅状态的变化可能
 * 需要触发积分的重新计算。通过将所有业务提供者集中在此，可以确保它们在
 * 一个可预测的、统一的上下文中初始化，并为未来可能出现的跨领域通信
 * （如通过共享的事件总线）提供一个稳定的基础。
 *
 * 当前，它集成了以下业务提供者：
 * 1. @readsavor/subscription: 负责监听订阅相关的实时事件。
 *
 * 未来，它将集成更多业务提供者，例如：
 * - @readsavor/credits
 * - @readsavor/coupon
 * - @readsavor/integration
 */

import React, { useEffect, useRef } from 'react';
import { SubscriptionProvider } from '@readsavor/subscription';
import { CreditProvider } from '@readsavor/credits';
import { useAuthStore } from '@readsavor/auth';
import { queryClient } from './AppServiceProviders';
import { CouponProvider } from '@readsavor/coupons';
import { SubscriptionLifecycleManager } from '../components/Subscription/SubscriptionLifecycleManager';

// --- 全局初始化 Hook ---
const useGlobalInitialization = () => {
  const isInitialized = useRef(false);

  useEffect(() => {
    if (isInitialized.current) {
      return;
    }

    // 将 queryClient 实例注入到 auth store 中
    useAuthStore.getState().initialize(queryClient);

    isInitialized.current = true;
  }, []);
};

// --- React 组件 ---

interface AppBusinessProviderProps {
  children: React.ReactNode;
}

/**
 * 统一的业务领域提供者组件
 *
 * @param {AppBusinessProviderProps} props - 组件属性
 * @returns {React.ReactElement} 组合了所有业务领域提供者的组件
 */
export const AppBusinessProvider: React.FC<AppBusinessProviderProps> = ({ children }) => {
  // 在组件生命周期内执行一次性初始化
  useGlobalInitialization();

  return (
    // 未来新的业务 Provider 将被添加到这里，
    // 例如 <CreditProvider>, <CouponProvider> 等。
    // 当前我们只集成了 SubscriptionProvider。
    <SubscriptionProvider>
      <CreditProvider>
        <CouponProvider>
          <SubscriptionLifecycleManager>{children}</SubscriptionLifecycleManager>
        </CouponProvider>
      </CreditProvider>
    </SubscriptionProvider>
  );
};
